import React, { useEffect } from 'react';
import Radio from '../../forms/Radio';
import Input from '../../forms/Input';
import { QuestRoute } from '../../api/QuestRoutes';
import Checkbox from '../../forms/Checkbox';
import Preput from '../../forms/Preput';
import Select from '../../forms/Select';
import { HSOptions } from '../../../constants/HSOptions';
import Table, { Ctd } from '../../table/Table';
import { generateId, statusColors, translate } from '../../utility/functions';
import Col from '../../layout/Col';
import Button from '../../forms/Button';
import GetState from '../../GetState';
import HamQuest from '../../api/HamQuestGiver';
import Form from '../../forms/Form';
import LocationSelectors from '../../forms/formComponents/LocationSelectors';
import TerminalIDsSelector from '../../forms/formComponents/TerminalIDsSelector';
import { IsAuthorized, userRoles } from '../../RoleProtected';
import Icon from '../../forms/Icon';
import ManagementRow from '../../table/ManagementRow';
import ModalFactory from '../../Modal';
import CheckMark from '../../CheckMark';
import { codeLookUp } from './Codes';
import UpgradeAndAutoSelect from '../../forms/formComponents/UpgradeAndAutoSelect';
import Page from '../../layout/Page';


const ReceiptCodes = ({ ...props }) => {

    const [receipts, refreshReceipts] = GetState(QuestRoute.codes.receipt);
    const [details] = GetState(QuestRoute.washClubs.detail);

    const ReceiptForm = ({ values, editing = false, ...props }) => (
        <>
            <Checkbox
                label='Enabled'
                name='enabled'
            />
            <Select
                label='Kiosk Item'
                name='itemId'
                getOptions={QuestRoute.kiosks.wash}
                buildOptions={e => {
                    e.label = e.name;
                    e.value = e.id;
                }}
                required
            />
            <Select
                label='Award Type'
                name='awardType'
                options={HSOptions.expressPass.receiptItemType}
                required
                disabled={editing}
            />
            <Input
                label={values.awardType === 'Time' ? 'Time (m)' : 'Discount Value'}
                name='discountValue'
                type={values.awardType === 'Time' ? 'number' : 'price'}
                step={values.awardType === 'Time' ? 1 : 0.01}
                min={values.awardType === 'Time' ? 1 : 0.01}
                required
            />

            {values.awardType === 'DollarDiscItem' && (
                <Radio
                    label='Discounted Item'
                    name='item'
                    getOptions={QuestRoute.kiosks.wash}
                    buildOptions={(e) => {
                        e.label = e.name;
                        e.value = e.id;
                    }}
                    required
                />
            )}

            {values.awardType === 'Time' && (
                <>
                    <b>Allowed Models</b>
                    {details.kioskSubtypes?.map((e, i) => (
                        <Checkbox
                            key={'kioskSubtypes' + i}
                            name='kioskSubTypeIds'

                            label={e.label}
                            value={e.value}
                        />
                    ))}
                </>
            )}

            <Input
                label='Max Uses'
                name='maxUses'
                type='number'
                min={0}
                step={1}
            />
            <Input
                label='Expires (Days)'
                name='expires'
                type='number'
                min={1}
                step={1}
            />

            <UpgradeAndAutoSelect 
                isAward={true} 
                autoSelectFieldName='autoSelect'
            />
            
            <LocationSelectors />
            <TerminalIDsSelector />

            <Preput
                label='Message'
                name='message'
                tags={['ExpirationDate']}
            />
        </>
    )

    const deleteCode = (code) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: `Are you sure you want to delete ${code.itemName}?`,
                type: 'warning',
            },
            promise: {
                title: 'Deleting Wash Club',
                body: `Deleting ${code.itemName}`,
                callback: () => HamQuest.delete(QuestRoute.codes.editReceipt(code.loyaltyAwardKioskReceiptItemId))
            },
            confirmed: {
                title: 'Success',
                body: `${code.itemName} successfully deleted.`,
                type: 'success'
            },
            finally: () => refreshReceipts()
        })
    }

    const editCode = (code) => {
        // console.log(code)
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Edit ${code.itemName}`,
            body: (
                <Form
                    inputs={{ ...code }}
                    onSubmit={e => HamQuest.put(QuestRoute.codes.editReceipt(e.loyaltyAwardKioskReceiptItemId), { ...e })
                        .then(() => {
                            refreshReceipts();
                            ModalFactory.close(id);
                        })
                    }
                    text='Update'
                >
                    {({ values }) => <ReceiptForm values={values} editing />}
                </Form>
            )
        })
    }

    const addNewCode = () => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Add a new Receipt Code`,
            body: (
                <Form
                    inputs={{
                        itemId: '',
                        awardType: '',
                        discountValue: '',
                        maxUses: '',
                        expires: '',
                        enabled: true,
                        allowUpgrades: '',
                        autoSelect: '',
                        locations: [],
                        terminalIds: [],
                        message: '',
                        kioskSubTypeIds: [],
                    }}
                    onSubmit={e => HamQuest.post(QuestRoute.codes.receipt, { ...e })
                        .then(() => {
                            refreshReceipts();
                            ModalFactory.close(id);
                        })
                    }
                    text='Add'
                >
                    {({ values }) => <ReceiptForm values={values} />}
                </Form>
            )
        })
    }

    const isAuth = IsAuthorized(userRoles.editProducts)

    const IssuedCodes = ({ ...props }) => {
        const defaults = [
            "AppAwardCode", "AppBundleCode", "AppDealCode",
            "AppPromoDealCode", "AppReferralCode", "AppWashClub",
            "AppWashClubTime", "AppWashCode", "BundleCode",
            "DailyPass", "FleetPass", "PrepaidPass",
            "ReceiptCode", "ReceiptCodeTime", "RewashCode", "WashCode"
        ]
        const [issueReceipts, _, setIssueReceipts] = GetState(QuestRoute.codes.issueReceipt, {
            loading: [],
            finalCall: (res) => {
                const configured = res.map(e => e.name);
                const enabled = false;
                defaults.forEach(name => {
                    if (configured.includes(name)) return;
                    res.push({ name, enabled });
                })
                return res;
            }
        })

        return (
            <Col>
                {issueReceipts.map((e, i) => (
                    <Checkbox
                        key={'issued' + i}
                        label={translate(e.name)}
                        value={e.enabled}
                        disabled={!isAuth}
                        onChange={() => {
                            HamQuest.put(QuestRoute.codes.issueReceipt, {
                                ...e,
                                enabled: !e.enabled
                            })
                                .then((res) => setIssueReceipts([...res]))
                        }}
                        standalone
                    />
                ))}
            </Col>
        )
    }

    const editIssued = () => {
        ModalFactory.show({
            title: 'Issues Receipt Codes',
            body: <IssuedCodes />
        })
    }

    return (
        <Page
            title='Receipt Codes'
            icon='QRcode'
            controls={<Button onClick={codeLookUp}>Look Up</Button>}
        >
        <Table
            config={{
                // label: 'Receipt Codes',
                data: receipts,
                controls: [
                    <Button roles={userRoles.editProducts} onClick={addNewCode}>Add New</Button>,
                    <Button roles={userRoles.editProducts} onClick={editIssued}>Issues Receipt Codes</Button>,
                    <Button onClick={codeLookUp}>Look Up</Button>
                ],
                headers: [
                    {
                        label: 'Kiosk Item',
                        propKey: 'itemName',
                        options: true,
                    }, {
                        label: 'Item SKU',
                        propKey: 'itemSku',
                        options: true,
                    }, {
                        label: 'Award Type',
                        propKey: 'awardType',
                        options: true,
                    }, {
                        label: 'Message',
                        propKey: 'message',
                    }, {
                        label: 'Enabled',
                        propKey: 'enabled',
                        options: true,
                    },
                ],
                renderFormat: (e) => (
                    <ManagementRow
                        key={generateId()}
                        controls={[
                            <Button roles={userRoles.editProducts} key={generateId()} onClick={() => editCode(e)}>Edit</Button>,
                            <Button roles={userRoles.editProducts} key={generateId()} onClick={() => deleteCode(e)}>Delete</Button>
                        ]}
                    >
                        <td>{e.itemName}</td>
                        <td>{e.itemSku}</td>
                        <td>{translate(e.awardType)}</td>
                        <td>{e.message}</td>
                        <Ctd><CheckMark checked={e.enabled} /></Ctd>
                    </ManagementRow>
                )
            }}
        />
        </Page>
    );
}

export default ReceiptCodes;